import { useEffect, useMemo, useRef, useState } from 'react';
import { useApp } from '../store';
import PickField from './PickField';
import { displayName, initials, initialsFromName } from '../people';
import { historyTime } from '../utils';
import { visibleComments } from '../comments';
import { canRemoveComment, canEditComment, can, type Role } from '../permissions';
import { nameMentions, resolveMentions } from '../mentions';
import { MentionInput } from './MentionInput';
import { useSampleBoard, useSampleInert } from './SampleStrip';
import IssueText from './IssueText';
import ReactionBar from './ReactionBar';
import Icon from './Icon';
import VendorTag from './VendorTag';
import {
  STATUS_LABELS,
  type Comment,
  type Issue,
  type IssueStatus,
} from '../types';
import { statusOrderFor } from '../lifecycle';
import { combo } from '../platform';
import { askStartGate } from '../start-gate';
import { outstandingChecks, doneAllowed } from '../done';
import { confirmDialog } from '../confirm';

/**
 * THE CARD FEED — Status log and Comments, shown as one "Updates & Comments"
 * stream (Vinnie 4 Oct 2026: merge them so a comment can reply to a specific
 * status). Built to dev-preview/card-feed/csm-147-updates-and-comments-after.png.
 *
 * Two stores, one feed. The top-level spine is the status log (`standsLog`): an
 * Update (tells the card's people), a Note (quiet), or a move. A reply is a
 * comment carrying the id of the entry it answers, threaded underneath. An answer
 * to the reporter is the existing server-side flow, shown as the one customer-
 * facing entry — and only on a card that has a reporter (FlightDeck's own support
 * channel; see reference-reporter-flow). Nothing is migrated; the card just draws
 * the two together.
 */

type FeedKind = 'move' | 'update' | 'note' | 'answer';

type FeedEntry = {
  id: string;
  at: number;
  by: string;
  text: string;
  kind: FeedKind;
  status?: IssueStatus; // a move carries the status it went to
  nextStep?: string;
  quiet?: boolean;
  auto?: boolean;
  removable?: boolean; // a comment, a note or a plain update can be withdrawn; a status MOVE cannot
  logEntry?: boolean; // from the status log (standsLog), not the comments table — removal routes differently
  editedAt?: number; // the words were changed after posting — shows a quiet mark
  withdrawn?: boolean; // a withdrawn note/update — shown only to a reviewer, marked and read-only
  withdrawnBy?: string; // who withdrew it, for the reviewer's marker
  withdrawnAt?: number; // when it was withdrawn
};

export default function CardFeed({
  issue,
  canPost,
  role,
  onDraft,
  inline,
  stagingEnabled = false,
}: {
  issue: Issue;
  canPost: boolean;
  role: Role;
  onDraft?: (hasDraft: boolean) => void;
  /** Whether THIS CARD'S board has the In Staging column turned on — passed in
   *  because the feed opens on cards from other boards (My Work), where the
   *  active board's setting would be the wrong one. The status picker offers the
   *  In Staging option only when this is true (or the card already sits there). */
  stagingEnabled?: boolean;
  /**
   * WHEN THE CARD IS OPEN IN THE MY WORK PANEL, on a board that is not the one
   * active behind it. The feed then posts and replies through these by-board-id
   * handlers instead of the active-board store actions, and the in-place edit,
   * withdraw and answer-the-reporter controls are withheld — those are board-
   * coupled and live on the full card (the panel's "Open full card" escape hatch).
   * Reactions need no board and work unchanged. Absent on the card's own board.
   */
  inline?: {
    post: (input: { text: string; status?: IssueStatus; nextStep?: string; quiet?: boolean; overrideNote?: string }) => Promise<void>;
    reply: (
      author: string,
      text: string,
      replyTo?: { parentId: string; parentAuthor: string }
    ) => Promise<string>;
  };
}) {
  const isInline = !!inline;
  const sample = useSampleBoard();
  const inert = useSampleInert();
  const profiles = useApp((s) => s.profiles);
  const myEmail = useApp((s) => s.user?.email ?? '');
  const emails = useApp((s) => s.access.emails);
  const others = useMemo(
    () => emails.filter((e) => e !== myEmail.toLowerCase()),
    [emails, myEmail]
  );
  const post = useApp((s) => s.postStatusUpdate);
  // The active board's cards, so the composer's status move can ask the same soft
  // start-gate the Board drag and the card modal ask — read here, not passed in,
  // because the gate only means anything on the card's OWN board (a blocker names
  // a same-board ref). Withheld on the inline My Work path, like the other board-
  // coupled controls.
  const allIssues = useApp((s) => s.issues);
  const depHardBlock = useApp((s) => s.meta.depHardBlock ?? false);
  // The active board's meta, for the Definition-of-Done gate below — the composer
  // move-to-Done must honour it exactly as Edit+Save does.
  const meta = useApp((s) => s.meta);
  const addComment = useApp((s) => s.addComment);
  const removeComment = useApp((s) => s.removeComment);
  const removeStatusUpdate = useApp((s) => s.removeStatusUpdate);
  const editComment = useApp((s) => s.editComment);
  const editStatusUpdate = useApp((s) => s.editStatusUpdate);
  const answerReport = useApp((s) => s.answerReport);
  const commentError = useApp((s) => s.commentError);
  const reporter = (useApp((s) => s.reporters[issue.id]) ?? '');
  // The reporter-answer flow is board-coupled (it reads the active board's
  // reporter) — withheld inline, where it belongs on the full card.
  const canAnswer = !isInline && !!reporter && can(role, 'answerReport');
  // A withdrawn note/update is hidden from everyone — unless they may moderate
  // others' entries (the same right that lets an admin withdraw anyone's), in
  // which case it stays visible, marked and read-only, so what was taken down can
  // still be reviewed.
  const canReview = can(role, 'removeAnyComment');
  // Inline reply returns its reason (there is no optimistic row to take back),
  // so it is shown here rather than through the store's commentError.
  const [inlineErr, setInlineErr] = useState('');

  // The composer.
  const [text, setText] = useState('');
  const [sel, setSel] = useState<IssueStatus>(issue.status);
  // In Staging is offered only where this card's board uses it, or where the card
  // already sits there (so the picker never hides its own current value).
  const statusChoices = statusOrderFor({ stagingEnabled: stagingEnabled || sel === 'staging' });
  const [quiet, setQuiet] = useState(false);
  // The status picker IS the card's own PickField — one status control app-wide
  // (Vinnie, 17 Oct), so the composer's "record a status move" menu reads exactly
  // like the Status field on the card. Its menu is bound to the card's scroll area
  // so, sitting low in the card, it flips up to fit rather than spilling past the
  // footer; the ref is filled from the field's nearest .modal-body on mount.
  const statusBoundRef = useRef<HTMLElement | null>(null);
  const [showNext, setShowNext] = useState(false);
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);

  // Per-entry reply drafts and which threads are open.
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [replyText, setReplyText] = useState<Record<string, string>>({});
  const [removing, setRemoving] = useState<string | null>(null);
  // Which entry is being edited in place, and the words so far. One at a time —
  // the id is enough, because the card only ever opens one editor.
  const [editing, setEditing] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  // What the entry said when the editor opened — so Save can refuse an edit that
  // changes nothing, not only an empty one (a no-op click looked like it saved).
  const [editOriginal, setEditOriginal] = useState('');
  // Editing a status-log entry's NEXT STEP in the same box — the composer's field,
  // reused. `editShowNext` shows the row (open when the entry already has one, or
  // when "+ Next step" is pressed); `editNextOriginal` lets Save spot a change to
  // the next step alone. Comments have no next step, so this stays closed for them.
  const [editNext, setEditNext] = useState('');
  const [editShowNext, setEditShowNext] = useState(false);
  const [editNextOriginal, setEditNextOriginal] = useState('');

  // The folded-in answer-to-the-reporter box — collapsed until asked for, so an
  // external email is never one stray click away.
  const [answerOpen, setAnswerOpen] = useState(false);
  const [answer, setAnswer] = useState('');
  const [answering, setAnswering] = useState(false);
  const [answerNote, setAnswerNote] = useState('');

  // The picker is a "change status" control that always opens on the current
  // column — re-sync whenever the card moves, including after our own post.
  useEffect(() => {
    setSel(issue.status);
  }, [issue.status]);

  // A fresh card starts with nothing typed anywhere.
  useEffect(() => {
    setText('');
    setQuiet(false);
    setShowNext(false);
    setNext('');
    setOpen(new Set());
    setReplyText({});
    setEditing(null);
    setEditText('');
    setAnswer('');
    setAnswerNote('');
    setAnswerOpen(false);
    setInlineErr('');
    // Clear a stale post error left by a DIFFERENT context — `commentError` is a
    // shared store field (a failed "raise a new issue" sets it too, store.ts), and
    // a freshly-opened card should not greet you with "Not posted — …" from an
    // action that had nothing to do with this card's feed. A real failure on THIS
    // composer re-sets it the moment it happens.
    if (useApp.getState().commentError) useApp.setState({ commentError: '' });
  }, [issue.id]);

  const moved = sel !== issue.status;

  // Tell the parent whenever something is typed-but-unsent, so closing the card
  // can warn rather than bin it (the comment box's own precedent — ledger L54).
  const anyReply = Object.values(replyText).some((v) => v.trim() !== '');
  const hasDraft =
    text.trim() !== '' ||
    (showNext && next.trim() !== '') ||
    anyReply ||
    editing !== null ||
    answer.trim() !== '';
  useEffect(() => {
    onDraft?.(hasDraft);
    return () => onDraft?.(false);
  }, [hasDraft, onDraft]);

  // ── Build the one feed from the two stores ─────────────────────────────────
  /*
   * MEMOISED, OR THE TWO MEMOS BELOW ARE NOT MEMOS. visibleComments is a
   * `.filter` (src/comments.ts), so it hands back a NEW array every call — and
   * both `repliesByParent` and `topLevel` list it as a dependency. Computed
   * bare, its identity changed on every render, so neither of them ever cached
   * and every render rebuilt the replies Map, sorted each thread, and re-sorted
   * the whole merged feed. `issue.comments` is the right key: it is a stable
   * store reference (the card comes from s.inlineCard or s.issues), so it
   * changes exactly when the comments really change.
   */
  const comments = useMemo(() => visibleComments(issue.comments), [issue.comments]);
  const repliesByParent = useMemo(() => {
    const m = new Map<string, Comment[]>();
    for (const c of comments) {
      if (!c.parentId) continue;
      const list = m.get(c.parentId) ?? [];
      list.push(c);
      m.set(c.parentId, list);
    }
    for (const list of m.values()) list.sort((a, b) => a.createdAt - b.createdAt);
    return m;
  }, [comments]);

  const topLevel: FeedEntry[] = useMemo(() => {
    const fromLog: FeedEntry[] = (issue.standsLog ?? [])
      // A withdrawn note/update is hidden like a removed comment — shown to no one
      // but a reviewer, who sees it marked and read-only so it can be looked back
      // over.
      .filter((e) => !e.removed || canReview)
      .map((e) => ({
        id: e.id,
        at: e.at,
        by: e.by,
        text: e.text,
        status: e.status,
        nextStep: e.nextStep,
        quiet: e.quiet,
        auto: e.auto,
        editedAt: e.editedAt,
        kind: e.status ? 'move' : 'update',
        logEntry: true,
        withdrawn: e.removed,
        withdrawnBy: e.removedBy,
        withdrawnAt: e.removedAt,
        // A note or a plain update is the author's to withdraw (a status MOVE is
        // the card's history and is not). Offered on the full card, not inline —
        // the same board-coupling as a comment — and not while it has replies, so
        // withdrawing one can never orphan the conversation beneath it, and not
        // once it is already withdrawn.
        removable: !isInline && !e.status && !e.auto && !e.removed && !repliesByParent.has(e.id),
      }));
    const fromComments: FeedEntry[] = comments
      .filter((c) => !c.parentId)
      .map((c) => ({
        id: c.id,
        at: c.createdAt,
        by: c.author,
        text: c.text,
        kind: c.toReporter ? 'answer' : 'note',
        // Withdrawing a comment is board-coupled (a targeted edit on the card's
        // board) — withheld inline, offered on the full card.
        removable: !isInline,
        editedAt: c.editedAt,
      }));
    return [...fromLog, ...fromComments].sort((a, b) => b.at - a.at); // newest first
  }, [issue.standsLog, comments, isInline, repliesByParent, canReview]);

  // A withdrawn update does not count — the headline reads what is actually shown,
  // the same way the comment count skips removed comments.
  const updateCount = (issue.standsLog ?? []).filter((e) => !e.auto && !e.removed).length;
  const answerCount = comments.filter((c) => !c.parentId && c.toReporter).length;

  const canSubmit =
    canPost && !busy && (text.trim() !== '' || (showNext && next.trim() !== '') || moved);

  const submit = async () => {
    if (!canSubmit) return;
    const sent = { text, next, showNext, quiet, status: moved ? sel : undefined };
    // THE SOFT START-GATE — the Status ▾ + Post control is the third deliberate-
    // start surface (the Board drag and the card modal are the others). Moving a
    // card into a started column while it still waits on an unfinished predecessor
    // WARNS, it does not refuse: Cancel aborts the whole post (the draft stays, so
    // the gate runs BEFORE any field is cleared); "Start anyway" posts and records
    // the override. Only on the card's own board — the inline panel has no reliable
    // predecessor set, so it posts straight, like its other board-coupled controls.
    let overrideNote: string | undefined;
    if (!inline && sent.status) {
      const byRef = new Map(allIssues.map((c) => [c.ref, c] as const));
      const gate = await askStartGate(issue, sent.status, byRef, displayName(myEmail, profiles) || myEmail, depHardBlock);
      if (!gate.proceed) return; // cancelled — nothing is posted, the draft is kept
      overrideNote = gate.overrideNote;
    }
    // THE DEFINITION-OF-DONE GATE — the composer's move-to-Done must honour it the
    // same as Edit+Save (IssueModal), or "done" means two different things on one
    // board. A hard refuse (the board's own copy promises it is), acknowledge-only,
    // leaving the draft so nothing typed is lost (L161). On the card's own board
    // only: an inline cross-board panel has the wrong board's meta to judge by.
    if (!inline && sent.status === 'done' && !doneAllowed(issue, meta)) {
      // A fully-ticked card checklist clears the gate in place of the DoD
      // (doneAllowed); otherwise the outstanding checks are named and the post held.
      const missing = outstandingChecks(issue, meta);
      await confirmDialog({
        title: 'Not ready to be done',
        body: `Still to do before this can be done: ${missing.join(', ')}.`,
        confirmLabel: 'OK',
        tone: 'caution',
        alert: true,
      });
      return; // not posted — the draft stays
    }
    setBusy(true);
    setText('');
    setNext('');
    setShowNext(false);
    try {
      const payload = {
        text: resolveMentions(sent.text.trim(), others, profiles),
        status: sent.status,
        nextStep: sent.showNext ? sent.next.trim() : undefined,
        quiet: sent.quiet,
        overrideNote,
      };
      if (inline) await inline.post(payload);
      else await post(issue.id, payload);
    } catch {
      setText(sent.text);
      setNext(sent.next);
      setShowNext(sent.showNext);
    } finally {
      setBusy(false);
    }
  };

  const toggleThread = (id: string) =>
    setOpen((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  // Cancel a reply: close just that reply box and drop its draft, so Escape and
  // the Cancel button give a way back without closing the whole card.
  const closeReply = (id: string) => {
    setOpen((s) => {
      const n = new Set(s);
      n.delete(id);
      return n;
    });
    setReplyText((r) => ({ ...r, [id]: '' }));
  };

  const sendReply = (parentId: string, parentAuthor: string) => {
    const body = (replyText[parentId] ?? '').trim();
    if (!body) return;
    const resolved = resolveMentions(body, others, profiles);
    if (inline) {
      // No optimistic row to take back across boards — the by-id reply returns its
      // reason, shown inline; the draft is only cleared once it has gone.
      setInlineErr('');
      void inline.reply(myEmail, resolved, { parentId, parentAuthor }).then((why) => {
        if (why) setInlineErr(why);
        else setReplyText((r) => ({ ...r, [parentId]: '' }));
      });
      return;
    }
    addComment(issue.id, myEmail, resolved, { parentId, parentAuthor });
    setReplyText((r) => ({ ...r, [parentId]: '' }));
  };

  // Who may edit an entry: its AUTHOR, and only the author — rewording what a
  // person said is theirs alone, admin included (removing is a separate right).
  // The app's own auto lines and the emailed answer to the customer are never
  // editable in place.
  const canEditEntry = (by: string, kind: FeedKind, auto?: boolean) =>
    !isInline && kind !== 'answer' && !auto && canEditComment(role, by, myEmail);

  const beginEdit = (entryId: string, current: string, currentNext?: string) => {
    setRemoving(null);
    setEditing(entryId);
    // Seed the box the way the entry READS — @mentions as names — not the raw
    // stored @address. resolveMentions turns the names back into addresses on
    // save, so an untouched entry stores byte-identical text. editOriginal takes
    // the SAME name form, or change-detection would flag an untouched edit.
    const shown = nameMentions(current, profiles);
    setEditText(shown);
    setEditOriginal(shown);
    // Seed the next step too (status-log entries only — a comment passes none).
    // The row opens when there is already a next step to edit; otherwise it waits
    // behind a quiet "+ Next step".
    const nx = currentNext ?? '';
    setEditNext(nx);
    setEditNextOriginal(nx);
    setEditShowNext(nx.trim() !== '');
  };
  const cancelEdit = () => {
    setEditing(null);
    setEditText('');
    setEditOriginal('');
    setEditNext('');
    setEditNextOriginal('');
    setEditShowNext(false);
  };
  // A comment (or reply) is edited through the comments store; a status-log entry
  // through the issue. Mentions resolve on save, exactly as they do when posting.
  const saveEdit = (entryId: string, isComment: boolean) => {
    const body = resolveMentions(editText.trim(), others, profiles);
    if (body) {
      if (isComment) editComment(issue.id, entryId, body);
      // A status-log entry carries its next step too — '' when the row is hidden,
      // which clears a next step that was removed.
      else void editStatusUpdate(issue.id, entryId, body, editShowNext ? editNext.trim() : '');
    }
    cancelEdit();
  };

  // The in-place editor, shared by a top-level entry and a reply so the two look
  // and behave the same. Built to dev-preview/edit-feed (Vinnie signed off 4 Oct).
  const renderEditor = (entryId: string, isComment: boolean) => {
    // Save when the words changed OR — for a status-log entry — the next step did
    // (including being added or cleared), never on a no-op, never on blank words.
    const textChanged = editText.trim() !== editOriginal.trim();
    const nextNow = !isComment && editShowNext ? editNext.trim() : '';
    const nextChanged = !isComment && nextNow !== (editNextOriginal ?? '').trim();
    const canSave = !!editText.trim() && (textChanged || nextChanged);
    return (
      <div className="feed-editbox">
        {/* The same mention-aware field the composer uses, so "@ to mention
            someone" below is now true here too — it was a plain textarea with a
            footer promising a picker that was not there. Multiline, because an
            entry can run to several lines; Escape bows out keeping the words,
            Enter saves, Shift+Enter is a new line. */}
        <MentionInput
          multiline
          autoFocus
          className="ctl"
          ariaLabel="Edit the text"
          placeholder=""
          value={editText}
          onChange={setEditText}
          onSubmit={() => saveEdit(entryId, isComment)}
          onCancel={cancelEdit}
        />
        {/* The next step edits here too, in the composer's own field — a
            status-log entry only (a comment has no next step). Pre-filled when the
            entry has one; otherwise offered behind "+ Next step". */}
        {!isComment &&
          (editShowNext ? (
            <div className="statuslog-nextrow">
              <span className="statuslog-nextarrow" aria-hidden="true">→</span>
              <input
                className="ctl statuslog-next-in"
                placeholder="What happens next?"
                value={editNext}
                aria-label="Next step"
                onChange={(e) => setEditNext(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    saveEdit(entryId, isComment);
                  }
                }}
              />
              <button
                type="button"
                className="btn-icon statuslog-dropnext"
                aria-label="Remove next step"
                onClick={() => {
                  setEditShowNext(false);
                  setEditNext('');
                }}
              >
                ✕
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="btn statuslog-addnext"
              onClick={() => setEditShowNext(true)}
            >
              + Next step
            </button>
          ))}
        <div className="feed-editfoot">
          <span className="hint">@ to mention · Return for a new line · {combo('B')} to bold · {combo('↵')} to save</span>
          <button type="button" className="btn btn-small" onClick={cancelEdit}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-small btn-primary"
            disabled={!canSave}
            onClick={() => saveEdit(entryId, isComment)}
          >
            Save
          </button>
        </div>
      </div>
    );
  };

  const sendAnswer = () => {
    const body = answer.trim();
    if (!body) return;
    setAnswering(true);
    setAnswerNote('');
    answerReport(issue.id, body)
      .then((said) => {
        setAnswer('');
        setAnswerNote(
          said.emailed
            ? `Sent to ${reporter}.`
            : `Saved on this card, but the email did not go — ${said.why ?? 'no reason given'}`
        );
      })
      .catch((e: Error) => setAnswerNote(`Not sent — ${e.message}`))
      .finally(() => setAnswering(false));
  };

  const count = topLevel.length;

  return (
    <section className="statuslog cardfeed">
      <div className="statuslog-h">
        <h3>Updates &amp; Comments</h3>
        <span className="statuslog-count">
          {updateCount} {updateCount === 1 ? 'update' : 'updates'}
          {answerCount > 0 && ` · ${answerCount} to the customer`}
        </span>
        {/* ON HOLD — the SAME red badge the open card's header shows for a
            deliberately paused card (project-card-hold): both sit in one modal, so
            they must be one shape (R-badge) — `.rag.r`, not the card-FACE flag loz.
            The reason rides on hover. */}
        {issue.heldAt != null && (
          <span className="rag r" title={issue.holdReason || 'On Hold'}>
            <span className="dot" />
            On Hold
          </span>
        )}
      </div>
      <p className="statuslog-sub">
        Where the work stands, and the conversation around it — newest first.
      </p>

      {canPost && (
        <div className="statuslog-compose">
          <MentionInput
            multiline
            autoGrow
            className="ctl statuslog-text"
            value={text}
            onChange={setText}
            onSubmit={() => void submit()}
            placeholder="Post an update, or leave a note…"
          />
          <div className="feed-tools">
            <div className="seg feed-kind" role="group" aria-label="Who hears about this post">
              <button
                type="button"
                className={`seg-btn k-update ${!quiet ? 'active' : ''}`}
                aria-pressed={!quiet}
                title="Notifies the people on this card"
                onClick={() => setQuiet(false)}
              >
                <Icon name="bell" size={14} />
                Update
              </button>
              <button
                type="button"
                className={`seg-btn k-note ${quiet ? 'active' : ''}`}
                aria-pressed={quiet}
                title="Recorded quietly — no one is notified"
                onClick={() => setQuiet(true)}
              >
                <Icon name="pencil" size={14} />
                Note
              </button>
            </div>
            <span
              className="feed-status-field"
              ref={(el) => {
                // Bind the menu to the card's scroll area so it flips up to fit
                // when the composer sits low (null outside a modal → window).
                statusBoundRef.current = el?.closest<HTMLElement>('.modal-body') ?? null;
              }}
            >
              <span className="feed-status-lbl">Status</span>
              <PickField
                ariaLabel="Record a status move"
                value={sel}
                options={statusChoices.map((s) => ({
                  value: s,
                  label: STATUS_LABELS[s],
                  mark: <span className={`status-dot dot-${s}`} aria-hidden="true" />,
                }))}
                onChange={(s) => setSel(s as IssueStatus)}
                boundaryRef={statusBoundRef}
              />
            </span>
            {!showNext && (
              <button type="button" className="btn statuslog-addnext" onClick={() => setShowNext(true)}>
                + Next step
              </button>
            )}
            <button
              type="button"
              disabled={!canSubmit}
              {...inert('btn btn-primary statuslog-post', () => void submit())}
            >
              Post
            </button>
          </div>
          {showNext && (
            <div className="statuslog-nextrow">
              <span className="statuslog-nextarrow" aria-hidden="true">→</span>
              <input
                className="ctl statuslog-next-in"
                placeholder="What happens next?"
                value={next}
                autoFocus
                aria-label="Next step"
                onChange={(e) => setNext(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    void submit();
                  }
                }}
              />
              <button
                type="button"
                className="btn-icon statuslog-dropnext"
                aria-label="Remove next step"
                onClick={() => {
                  setShowNext(false);
                  setNext('');
                }}
              >
                ✕
              </button>
            </div>
          )}
          <p className="statuslog-rule hint">
            Change the <strong>Status</strong> to record a move, or post an <strong>Update</strong> —
            both notify the people on this card. A <strong>Note</strong> is added quietly.
            {' '}<strong>Return</strong> starts a new line and carries a list on;{' '}
            <strong>{combo('B')}</strong> makes the selected text bold; press{' '}
            <strong>{combo('↵')}</strong> or the button to post.
            {canAnswer && (
              <>
                {' '}
                To write the customer, use <strong>Answer {reporter}</strong> below — that emails them.
              </>
            )}
          </p>
        </div>
      )}

      {(isInline ? inlineErr : commentError) && (
        <div className="login-error feed-error">Not posted — {isInline ? inlineErr : commentError}</div>
      )}

      {/* The answer-to-the-reporter box, folded in but collapsed — an external
          email is never one stray click away. */}
      {canAnswer &&
        (!answerOpen ? (
          <button type="button" {...inert('btn feed-answer-open', () => setAnswerOpen(true))}>
            ✉ Answer {reporter}
          </button>
        ) : (
          <div className="answer-box feed-answer">
            <>
              <div className="answer-head">
                <span className="answer-title">Answer {reporter}</span>
                <span className="hint">They get this by email. Everything else here stays between us.</span>
              </div>
              <textarea
                className="input feedback-box"
                placeholder="We have found it — here is what to do in the meantime…"
                value={answer}
                autoFocus
                onChange={(e) => setAnswer(e.target.value)}
                disabled={answering}
                readOnly={sample.readOnly}
              />
              {answerNote && (
                <p className="hint answer-note" role="status">
                  {answerNote}
                </p>
              )}
              <div className="answer-foot">
                <button type="button" className="btn" disabled={answering} onClick={() => setAnswerOpen(false)}>
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={answering || !answer.trim()}
                  {...inert('btn btn-primary', sendAnswer)}
                >
                  {answering ? 'Sending…' : 'Send reply'}
                </button>
              </div>
            </>
          </div>
        ))}

      {count === 0 ? (
        <p className="statuslog-empty">
          Nothing yet. Post the first update to start the feed.
        </p>
      ) : (
        <ol className="statuslog-timeline">
          {topLevel.map((e) => {
            const replies = repliesByParent.get(e.id) ?? [];
            const isOpen = open.has(e.id);
            // The shown author, resolved once: the app's own lines carry no
            // author and read as "FlightDeck", and the avatar is taken from this
            // same label so the two can never disagree (was name "FlightDeck"
            // beside initials "SO").
            const who = e.by ? displayName(e.by, profiles) : 'FlightDeck';
            // What this entry IS, so the Remove control names it truthfully — a
            // comment, or (from the status log) the quiet note or the update it
            // actually is.
            const noun = e.logEntry ? (e.quiet ? 'note' : 'update') : 'comment';
            return (
              <li
                key={e.id}
                className={`statuslog-entry feed-${e.kind} tone-${e.status ?? 'note'}${
                  e.kind === 'answer' ? ' feed-emailed' : ''
                }${e.withdrawn ? ' feed-withdrawn' : ''}`}
              >
                <span className="statuslog-dot" aria-hidden="true" />
                <div className="statuslog-body">
                  {e.kind === 'answer' && (
                    <div className="feed-answerlead">↑ Answer to the customer · emailed</div>
                  )}
                  <div className="statuslog-when">
                    <span className="comment-av">{initialsFromName(who)}</span>
                    <strong>{who}</strong>
                    {e.by && <VendorTag who={e.by} profiles={profiles} compact />}
                    {e.kind === 'answer' && reporter && (
                      <span className="feed-answered"> answered {reporter}</span>
                    )}
                    {' · '}
                    <span className="statuslog-time">{historyTime(e.at)}</span>
                    {e.editedAt && !e.withdrawn && (
                      <span className="feed-edited" title={`Edited ${historyTime(e.editedAt)}`}>
                        · edited
                      </span>
                    )}
                    {e.withdrawn && (
                      // Reviewer-only: this entry was taken back. Marked and read-
                      // only — no reply, edit or remove — but its words stay here to
                      // be looked over.
                      <span
                        className="feed-withdrawnmark"
                        title={e.withdrawnAt ? `Withdrawn ${historyTime(e.withdrawnAt)}` : undefined}
                      >
                        · withdrawn
                        {e.withdrawnBy ? ` by ${displayName(e.withdrawnBy, profiles)}` : ''}
                      </span>
                    )}
                    {editing !== e.id && !e.withdrawn && (
                      <>
                        {canPost && (e.kind === 'update' || e.kind === 'move' || e.kind === 'note') && (
                          <button
                            type="button"
                            aria-expanded={open.has(e.id)}
                            {...inert('feed-reply', () => toggleThread(e.id))}
                          >
                            Reply
                          </button>
                        )}
                        {canEditEntry(e.by, e.kind, e.auto) && (
                          <button
                            type="button"
                            aria-label="Edit this entry"
                            {...inert('feed-edit', () => beginEdit(e.id, e.text))}
                          >
                            Edit
                          </button>
                        )}
                        {e.removable && canRemoveComment(role, e.by, myEmail) && removing !== e.id && (
                          <button
                            type="button"
                            aria-label={`Remove the ${noun} by ${who}`}
                            {...inert('comment-remove', () => setRemoving(e.id))}
                          >
                            Remove
                          </button>
                        )}
                      </>
                    )}
                  </div>
                  <div className="statuslog-what">
                    {e.kind === 'move' && e.status && (
                      // The move reads as a small heading; any note the mover left
                      // renders below it as its own block (a list, a line, whatever
                      // they wrote) — so no inline "." joins a heading to a block.
                      <strong className="statuslog-moved">Moved to {STATUS_LABELS[e.status]}</strong>
                    )}
                    {editing === e.id ? (
                      // A status-log note/update edits through editStatusUpdate, a
                      // comment through editComment — keyed on where it lives, not
                      // on whether it is removable (both now can be).
                      renderEditor(e.id, !e.logEntry)
                    ) : (
                      e.text && <IssueText text={e.text} mentions={myEmail} />
                    )}
                  </div>
                  {e.nextStep && (
                    <div className="statuslog-nextchip">
                      <span aria-hidden="true">→</span> Next: {e.nextStep}
                    </div>
                  )}
                  {e.kind === 'answer' && reporter && (
                    <div className="feed-marker feed-marker-mail">✉ Emailed to {reporter}</div>
                  )}

                  {removing === e.id && (
                    <div className="comment-confirm">
                      <span>Remove this {noun}? It stops being shown here. Nothing else changes.</span>
                      <button
                        type="button"
                        className="btn btn-small btn-danger"
                        onClick={() => {
                          // A status-log note or update goes through its own
                          // withdrawal (it is not a comments-table row); a comment
                          // or reply through removeComment.
                          if (e.logEntry) void removeStatusUpdate(issue.id, e.id, myEmail);
                          else removeComment(issue.id, e.id, myEmail);
                          setRemoving(null);
                        }}
                      >
                        Remove
                      </button>
                      <button type="button" className="btn btn-small" onClick={() => setRemoving(null)}>
                        Keep it
                      </button>
                    </div>
                  )}

                  {/* Reactions — a quiet row on the entry (comments and updates
                      alike), not on the app's own auto lines, and not on a
                      withdrawn one. */}
                  {e.kind !== 'answer' && !e.auto && !e.withdrawn && (
                    <ReactionBar issueId={issue.id} targetId={e.id} canReact={canPost && !sample.readOnly} />
                  )}

                  {/* Replies — collapsed to a count until opened. */}
                  {replies.length > 0 && (
                    <button type="button" className="feed-threadtoggle" onClick={() => toggleThread(e.id)}>
                      <span className={`feed-chev ${isOpen ? 'open' : ''}`} aria-hidden="true">›</span>
                      {replies.length} {replies.length === 1 ? 'reply' : 'replies'}
                    </button>
                  )}
                  {isOpen && (
                    <div className="feed-thread">
                      {replies.map((r) => (
                        <div key={r.id} className="feed-replyrow">
                          <span className="comment-av">{initials(r.author, profiles)}</span>
                          <div className="feed-replybody">
                            <div className="statuslog-when">
                              <strong>{displayName(r.author, profiles)}</strong>
                              <VendorTag who={r.author} profiles={profiles} compact />
                              {' · '}
                              <span className="statuslog-time">{historyTime(r.createdAt)}</span>
                              {r.editedAt && (
                                <span className="feed-edited" title={`Edited ${historyTime(r.editedAt)}`}>
                                  · edited
                                </span>
                              )}
                              {editing !== r.id && (
                                <>
                                  {canEditEntry(r.author, 'note') && (
                                    <button
                                      type="button"
                                      aria-label="Edit this reply"
                                      {...inert('feed-edit', () => beginEdit(r.id, r.text))}
                                    >
                                      Edit
                                    </button>
                                  )}
                                  {canRemoveComment(role, r.author, myEmail) && removing !== r.id && (
                                    <button
                                      type="button"
                                      aria-label={`Remove the reply by ${displayName(r.author, profiles)}`}
                                      {...inert('comment-remove', () => setRemoving(r.id))}
                                    >
                                      Remove
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                            <div className="statuslog-what">
                              {editing === r.id ? (
                                renderEditor(r.id, true)
                              ) : (
                                <IssueText text={r.text} mentions={myEmail} />
                              )}
                            </div>
                            <ReactionBar issueId={issue.id} targetId={r.id} canReact={canPost && !sample.readOnly} />
                            {removing === r.id && (
                              <div className="comment-confirm">
                                <span>Remove this reply? It stops being shown here.</span>
                                <button
                                  type="button"
                                  className="btn btn-small btn-danger"
                                  onClick={() => {
                                    removeComment(issue.id, r.id, myEmail);
                                    setRemoving(null);
                                  }}
                                >
                                  Remove
                                </button>
                                <button type="button" className="btn btn-small" onClick={() => setRemoving(null)}>
                                  Keep it
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                      {canPost && (
                        <>
                          <div className="feed-replybox">
                            <span className="comment-av as-me">{initials(myEmail, profiles)}</span>
                            <MentionInput
                              multiline
                              autoGrow
                              autoFocus
                              ariaLabel="Reply to this thread"
                              value={replyText[e.id] ?? ''}
                              onChange={(v) => setReplyText((r) => ({ ...r, [e.id]: v }))}
                              onSubmit={() => sendReply(e.id, e.by)}
                              // Escape collapses the reply but KEEPS the draft — a typed
                              // reply is real work (the same reason the composer's Escape
                              // only blurs). The Cancel button is the deliberate discard.
                              onCancel={() => toggleThread(e.id)}
                              placeholder="Reply to this thread…"
                            />
                            <button
                              type="button"
                              className="btn btn-small"
                              onClick={() => closeReply(e.id)}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              disabled={!(replyText[e.id] ?? '').trim()}
                              {...inert('btn', () => sendReply(e.id, e.by))}
                            >
                              Reply
                            </button>
                          </div>
                          <span className="hint feed-replyhint">
                            {combo('↵')} to reply · Return for a new line
                          </span>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
